import { FormattedMessage } from 'gatsby-plugin-intl';
import * as React from 'react';
import { Col, Grid, Row } from 'react-styled-flexboxgrid';
import styled, { keyframes } from 'styled-components';

import SvgHex from '../../../styles/static/hex.svg';
import { media } from '../../../styles/theme';
import { ButtonPrimary } from '../../common/Buttons';
import { Flex } from '../../common/Flex';
import { Icon } from '../../common/Icon';
import { Indent } from '../../common/Indent';
import { StyledText } from '../../common/StyledText';
import { AuthDrawerEvent } from '../../common/AuthDrawer';

const imacSrc = require('../../../styles/static/imac.png');
const imacSrc2x = require('../../../styles/static/imac@2x.png');
const imacGlareSrc = require('../../../styles/static/imacGlare.png');
const imacGlareSrc2x = require('../../../styles/static/imacGlare@2x.png');

export const Lead = () => {
  const [laptopIsLoaded, setLaptopIsLoaded] = React.useState(false);
  const laptopRef = React.useRef<HTMLImageElement>();
  const glareRef = React.useRef<any>();

  React.useEffect(() => {
    if (laptopRef.current?.complete) {
      setLaptopIsLoaded(true);
    }
  }, []);

  React.useEffect(() => {
    const handleScroll = () => {
      const glareEl = glareRef.current;
      if (!glareEl) return;

      const { scrollTop, clientTop } = document.documentElement;
      const scrollPosition = (window.pageYOffset || scrollTop) - (clientTop || 0);
      const paralaxEffect = Math.min(scrollPosition * 0.2, 100);
      glareEl.style.transform = `translate(${paralaxEffect}px, ${paralaxEffect * -0.35}px)`;
    };

    if (laptopIsLoaded) {
      window.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [laptopIsLoaded]);
  return (
    <FirstSection>
      <Grid>
        <Indent bottom={4.5} width='100%'>
          <Row>
            <Col xs={12} md={5}>
              <Row>
                <Col xs={12}>
                  <Indent bottom={2} top={2} smTop={4} width='100%'>
                    <Flex direction='column' align='center' mdAlign='flex-start'>
                      <StyledText
                        color='textInverted'
                        size='2.125'
                        smSize='2.625'
                        mdSize='2.125'
                        weight='400'
                        xsAlign='center'
                        mdAlign='left'>
                        <FormattedMessage id='homepage.firstSection.leadTitle' />
                      </StyledText>
                      <Indent vertical={0.5} type='block'>
                        <Flex direction='column' align='center' mdAlign='flex-start'>
                          <StyledText
                            align='center'
                            mdAlign='left'
                            color='primary'
                            size='3'
                            mdSize='3.5'
                            lineHeight='1.2'
                            weight='400'>
                            <FormattedMessage id='homepage.firstSection.leadSecondString' />
                          </StyledText>
                          <br />
                          <StyledText
                            align='center'
                            mdAlign='left'
                            color='primary'
                            size='2'
                            smSize='2.625'
                            mdSize='2.125'
                            weight='400'>
                            <FormattedMessage id='homepage.firstSection.leadThirdString' />
                          </StyledText>
                          <br />
                        </Flex>
                      </Indent>
                      <StyledText
                        color='textSecondary'
                        size='1'
                        smSize='1.25'
                        weight='400'
                        align='center'
                        mdAlign='left'>
                        <FormattedMessage id='homepage.firstSection.leadDescription' />
                      </StyledText>
                    </Flex>
                  </Indent>
                </Col>
                <Col xs={12}>
                  <Flex justify='center' mdJustify='flex-start'>
                    <ButtonPrimary
                      style={{
                        width: 'auto',
                      }}
                      size='large'
                      onClick={() => {
                        AuthDrawerEvent.emit('registration');
                      }}>
                      <FormattedMessage id='homepage.firstSection.openAccountBtn' />
                    </ButtonPrimary>
                  </Flex>
                </Col>
              </Row>
            </Col>
            <Col xs={12} md={7}>
              <Indent top={2} style={{ position: 'relative' }}>
                {laptopIsLoaded && (
                  <LaptopGlare ref={glareRef}>
                    <img srcSet={`${imacGlareSrc}, ${imacGlareSrc2x} 2x`} alt='' />
                  </LaptopGlare>
                )}
                <Laptop>
                  <img
                    srcSet={`${imacSrc}, ${imacSrc2x} 2x`}
                    alt=''
                    ref={laptopRef}
                    onLoad={() => {
                      setLaptopIsLoaded(true);
                    }}
                  />
                </Laptop>
                {laptopIsLoaded && (
                  <Hex>
                    <SvgHex />
                    <Icon
                      name='addUser'
                      width='3em'
                      color='#124798'
                      style={{
                        position: 'relative',
                        top: '-4%',
                        left: '4%',
                      }}
                    />
                  </Hex>
                )}
              </Indent>
            </Col>
          </Row>
        </Indent>
      </Grid>
    </FirstSection>
  );
};

const firstSectionBackground = require('../../../styles/static/firstScreen.jpg');

const FirstSection = styled.div`
  background: url(${firstSectionBackground}) 0 0 no-repeat;
  background-size: cover;
  background-position: center;
  position: relative;
  z-index: 1;
  padding-top: 182px;
`;

const Laptop = styled.picture`
  transform: translateX(9%);
  display: none;

  @media (min-width: 26rem) {
    display: block;
  }

  ${media.md`transform: translateX(3%);`}
  ${media.lg`transform: translateX(9%);`}

  img {
    max-width: 100%;

    ${media.md`
      max-width: initial
    `}
  }
`;

const showGlare = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const LaptopGlare = styled.picture`
  position: absolute;
  top: -30px;
  left: 0;
  max-width: 50%;
  max-height: 50%;
  animation: ${showGlare} 300ms linear 1;

  img {
    max-width: 100%;

    ${media.md`
      max-width: initial
    `}
  }
`;

const fliphex = keyframes`
  from {
    transform: rotateY(-270deg);
  }

  to {
    transform: rotateY(0deg);
  }
`;

export const Hex = styled.div`
  width: 114px;
  height: 126px;
  position: absolute;
  bottom: 23%;
  left: 13%;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotateY(0deg);
  perspective-origin: center;
  animation: ${fliphex} 500ms ease-in-out;
  opacity: 0;

  ${media.md`left: 7%; opacity: 1`}
  ${media.lg`left: 13%;`}

  > svg {
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    max-width: 100%;
  }
`;
